/* Settings, starting with the half that decides whether the product works.
 *
 * The prototype had a Settings screen and this client had none, which was
 * survivable for everything except one thing: **there was no way, from inside
 * the app, to find out whether reminders would actually reach you.** You could
 * be signed in on a phone, with permission granted, a device registered and a
 * plan full of blocks, and the only way to learn that nothing would arrive was
 * for nothing to arrive.
 *
 * That is the failure this screen exists to make impossible. It answers three
 * questions, in the order somebody asks them:
 *
 *   1. **Am I allowed to be notified?** — the browser's permission, in words,
 *      with the button that asks when asking is still possible and the
 *      instructions when it is not.
 *   2. **Which devices will be told?** — every registered device, when it was
 *      last seen, and a way to remove one you no longer use. A device list is
 *      also the only honest way to notice that the phone you care about is
 *      *not* in it.
 *   3. **Prove it.** — a real reminder, through the real path, arriving in five
 *      seconds. Not a simulation: it goes through the same queue, the same
 *      socket and the same push delivery as a block starting at nine tomorrow.
 *
 * The reminder half came first because it is the half you cannot verify any
 * other way. The shape of the day — sleep, working hours, rituals — followed,
 * because switching clients had quietly taken away the only place somebody
 * could say when they sleep, and every one of those numbers has a default that
 * is a guess about a stranger's life. Activity types and the connections to
 * Jira, Todoist and Linear are what is left.
 */

import Icon from '../ui/Icon';
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, post, del, errorMessage } from '../api';
import { useSession } from '../session';
import { useToast } from '../ui/toast';
import { ask, ensure, startWorker } from '../ui/push';
import type { PushState } from '../ui/push';
import { ConfirmDialog } from '../ui/schedule';
import { startTour } from '../ui/Tour';
import Shape from './settings/Shape';
import Types from './settings/Types';
import Connections from './settings/Connections';
import Section from '../ui/Section';
import ThemeToggle from '../ui/ThemeToggle';

interface Device {
  id: string;
  kind: 'web' | 'expo';
  label: string | null;
  created_at: number;
  last_seen: number;
}

/** "3 minutes ago", "yesterday" — a raw timestamp is not an answer to "is this
    phone still the one getting my reminders". */
function ago(ms: number): string {
  const s = Math.max(0, Math.round((Date.now() - ms) / 1000));
  if (s < 90) return 'just now';
  const m = Math.round(s / 60);
  if (m < 60) return `${m} minutes ago`;
  const h = Math.round(m / 60);
  if (h < 24) return h === 1 ? 'an hour ago' : `${h} hours ago`;
  const d = Math.round(h / 24);
  return d === 1 ? 'yesterday' : `${d} days ago`;
}

const STATE_COPY: Record<PushState, { title: string; body: string; tone: 'good' | 'bad' | 'plain' }> = {
  granted: {
    title: 'Reminders are on for this device',
    body: 'It is registered below, and a reminder will arrive here even with the tab closed.',
    tone: 'good'
  },
  default: {
    title: 'Reminders are not switched on yet',
    body: 'Amon plans your day and then tells you when each thing starts — ten minutes before, ' +
          'on the hour, and again if you have not begun. None of that reaches you until this ' +
          'device is allowed to notify you.',
    tone: 'plain'
  },
  denied: {
    title: 'Reminders are blocked for this site',
    body: 'The switch is in the browser rather than in the app, and Amon cannot reach it: ' +
          'click the icon at the left of the address bar → Notifications → Allow, then reload. ' +
          'On a phone, it is in the site settings for amon-time.com.',
    tone: 'bad'
  },
  broken: {
    title: 'Allowed, but this device could not register',
    body: 'This is the one worth acting on: notifications are permitted and nothing will ' +
          'arrive anyway. Try again, and if it keeps failing, tell me what it says.',
    tone: 'bad'
  },
  unsupported: {
    title: 'This browser cannot receive reminders',
    body: 'Amon will still plan your day and the day screen still shows what is next, but ' +
          'nothing will reach you with the tab closed. On an iPhone, add Amon to your home ' +
          'screen first — notifications become available once it is installed.',
    tone: 'bad'
  },
  'sw-failed': {
    title: 'The background worker would not start',
    body: 'That worker is what delivers a reminder when the app is closed. A private window ' +
          'or a locked-down browser profile is the usual cause.',
    tone: 'bad'
  }
};

/* The state in a few words, for the line under the heading. Not the notice's
   own title: saying the same sentence twice a few pixels apart reads as a
   glitch, and the long version is right below whenever it is needed. When
   reminders are on there is no notice at all, so the heading carries the
   whole good news. */
const STATE_SHORT: Record<PushState, string> = {
  granted: 'Reminders are on for this device',
  default: 'off for this device — one tap to switch on',
  denied: 'blocked by the browser',
  broken: 'allowed, but not working',
  unsupported: 'not available in this browser',
  'sw-failed': 'not available here'
};

export default function SettingsScreen() {
  const { user, signOut } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const [state, setState] = useState<PushState | null>(null);
  const [reason, setReason] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);
  const [dropping, setDropping] = useState<Device | null>(null);

  /* `startWorker` rather than `ensure`, and the difference matters on screen.
     `ensure` answers "what is the permission", which on a device where the
     background worker cannot start at all is a true answer to the wrong
     question — the banner would say the worker failed while this panel said
     the permission was refused, both correct, side by side, disagreeing.
     Registering is idempotent, so asking the fuller question costs nothing. */
  useEffect(() => {
    let alive = true;
    void startWorker().then(r => { if (alive) { setState(r.state); setReason(r.reason); } });
    return () => { alive = false; };
  }, []);

  const devices = useQuery({
    queryKey: ['devices'],
    queryFn: async () => {
      const r = await get<{ devices: Device[] }>('/api/devices');
      if (!r.ok) throw new Error(errorMessage(r, 'Could not read your devices.'));
      return r.devices;
    }
  });

  const enable = async () => {
    setBusy(true);
    const r = await ask();
    setState(r.state);
    setReason(r.reason);
    setBusy(false);
    void qc.invalidateQueries({ queryKey: ['devices'] });
    if (r.state === 'granted') toast.say('This device is registered. Send yourself a test.', 'good');
  };

  const retry = async () => {
    setBusy(true);
    const r = await ensure();
    setState(r.state);
    setReason(r.reason);
    setBusy(false);
    void qc.invalidateQueries({ queryKey: ['devices'] });
  };

  /* The whole point of the screen. Goes through the real queue rather than
     showing a fake notification in the page: a test that does not use the
     delivery path proves nothing about the delivery path. */
  const test = useMutation({
    mutationFn: async () => {
      const r = await post<{ inSeconds: number }>('/api/reminders/test', { seconds: 5 });
      if (!r.ok) throw new Error(errorMessage(r, 'Could not send that.'));
      return r.inSeconds;
    },
    onSuccess: s => toast.say(
      `Sent. It should arrive in about ${s} seconds — on every device listed here, ` +
      'not only this one.', 'good'),
    onError: (e: Error) => toast.say(e.message, 'bad')
  });

  const forget = useMutation({
    mutationFn: async (d: Device) => {
      const r = await del(`/api/devices/${d.id}`);
      if (!r.ok) throw new Error(errorMessage(r, 'Could not remove that.'));
      return d;
    },
    onSuccess: d => {
      void qc.invalidateQueries({ queryKey: ['devices'] });
      toast.say(`${d.label || 'That device'} will not be told any more.`);
    },
    onError: (e: Error) => toast.say(e.message, 'bad')
  });

  const copy = state ? STATE_COPY[state] : null;
  const list = devices.data ?? [];

  return (
    <div className="screen">
      <header className="topbar">
        <div>
          <h1>Settings</h1>
          <p className="muted">{user?.email}</p>
        </div>
      </header>

      {/* Jump links, so a long settings page is a list of places rather than a
          scroll. Plain anchors: they work without script and a screen reader
          announces them as the navigation they are. */}
      <nav className="settings-jump" aria-label="Settings sections">
        <a href="#reminders-title"><Icon name="bell" size={15} />Reminders</a>
        <a href="#sleep-title"><Icon name="moon" size={15} />Your day</a>
        <a href="#types-title"><Icon name="tag" size={15} />Types</a>
        <a href="#calendars-title"><Icon name="plug" size={15} />Connections</a>
        <a href="#account-title"><Icon name="user" size={15} />Account</a>
      </nav>

      <Section id="reminders-title" icon="bell" title="Reminders"
               className={state === 'granted' ? 'tone-green' : state && state !== 'default' ? 'tone-red' : ''}
               meta={state ? STATE_SHORT[state] : 'checking this device…'}
               info={<>Amon plans your day and then tells you when each thing starts — ten minutes
                 before, on the hour, and again if you have not begun. The test below goes through
                 the same path a real reminder takes, so if it arrives, the real ones will.</>}>

        {copy && state !== 'granted' && (
          <div className={`notice${copy.tone === 'bad' ? ' notice-bad' : ''}`} role="status">
            <p><strong>{copy.title}</strong></p>
            <p className="muted small">{copy.body}{reason && state === 'broken' ? ` (${reason})` : ''}</p>
            <div className="inline">
              {state === 'default' && (
                <button className="btn btn-primary btn-sm" onClick={enable} disabled={busy}>
                  {busy ? 'Asking…' : 'Allow notifications'}
                </button>
              )}
              {state === 'broken' && (
                <button className="btn btn-primary btn-sm" onClick={retry} disabled={busy}>
                  {busy ? 'Trying…' : 'Try again'}
                </button>
              )}
            </div>
          </div>
        )}

        <div className="inline wrap">
          <button className="btn btn-primary btn-sm"
                  onClick={() => test.mutate()}
                  disabled={test.isPending || state === 'denied' || state === 'unsupported'
                    || state === 'sw-failed'}>
            <Icon name="send" size={15} />{test.isPending ? 'Sending…' : 'Send a test reminder'}
          </button>
          <span className="muted small">
            {state === 'denied' || state === 'unsupported' || state === 'sw-failed'
              /* Sending one would succeed on the server and arrive nowhere,
                 which is exactly the confusion this screen exists to remove. */
              ? 'Not until the problem above is fixed — it would be sent, and land nowhere.'
              : 'It arrives in about five seconds, through the same path a real reminder takes.'}
          </span>
        </div>

        <div className="subhead">
          <h3>Devices that get told</h3>
          <span className="muted small">{list.length === 1 ? '1 device' : `${list.length} devices`}</span>
        </div>

        {devices.isError && (
          <p className="auth-err" role="alert">{(devices.error as Error).message}</p>
        )}

        {!devices.isPending && list.length === 0 && (
          <p className="muted small">
            No device is registered, so a reminder has nowhere to go. Allow notifications above —
            and if you want them on your phone, open <strong>amon-time.com</strong> there and allow
            them on the phone too. Each device registers itself.
          </p>
        )}

        <ul className="item-list">
          {list.map(d => (
            <li key={d.id} className="item">
              <span className="item-icon" aria-hidden="true">
                <Icon name={d.kind === 'expo' || /android|iphone|mobile/i.test(d.label ?? '') ? 'smartphone' : 'monitor'} size={17} />
              </span>
              <div className="item-body">
                <strong>{d.label || (d.kind === 'expo' ? 'Phone app' : 'A browser')}</strong>
                <span className="muted small">last seen {ago(d.last_seen)}</span>
              </div>
              <div className="item-tools">
                <button className="btn btn-ghost btn-sm btn-bad" onClick={() => setDropping(d)}>
                  <Icon name="trash" size={16} />Forget it
                </button>
              </div>
            </li>
          ))}
        </ul>
      </Section>

      {/* The shape of the day comes before the account: sleep and working
          hours are the two numbers every plan is built from, and they had no
          screen at all in this client until now. */}
      <Shape />

      <Types />

      <Connections />

      <Section id="account-title" icon="user" title="Account" meta={user?.email}>
        <div className="item">
          <span className="item-icon" aria-hidden="true"><Icon name="palette" size={17} /></span>
          <div className="item-body">
            <strong>Theme</strong>
            <span className="muted small">Ember is warm charcoal and orange; Violet is black and purple.</span>
          </div>
          <div className="item-tools"><ThemeToggle /></div>
        </div>
        <div className="item">
          <span className="item-icon" aria-hidden="true"><Icon name="help" size={17} /></span>
          <div className="item-body">
            <strong>The tour</strong>
            <span className="muted small">Two minutes, and you can leave at any point.</span>
          </div>
          <div className="item-tools">
            <button className="btn btn-ghost btn-sm" onClick={() => startTour()}>
              <Icon name="play" size={14} />Show me again
            </button>
          </div>
        </div>
        <div className="inline">
          <button className="btn btn-ghost btn-sm" onClick={() => void signOut()}><Icon name="logout" size={16} />Sign out</button>
        </div>
      </Section>

      {dropping && (
        <ConfirmDialog
          title={`Forget ${dropping.label || 'this device'}?`}
          description="It stops receiving reminders. Opening Amon on it again and allowing
                       notifications puts it back."
          confirmLabel="Forget it"
          onClose={() => setDropping(null)}
          onConfirm={() => forget.mutateAsync(dropping)} />
      )}
    </div>
  );
}
